/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/sonner";
import { CalendarCheck, MessageSquareReply, Mail, MessageSquare, BadgeCheck } from "lucide-react";
import { fmtD, fmtDT } from "../../lib/date";

const SRC = { manual: ["手工", "manual"], sms: ["短信识别", "from SMS"], email: ["邮件识别", "from email"] };

/* 承诺付款日：chip（未到=绿 / 已过=红）→ 弹层填日期 + 备注 / 清除 */
export const PromiseChip = ({ r, zh, api, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(r.promiseDate || "");
  const [note, setNote] = useState(r.promiseNote || "");
  const [busy, setBusy] = useState(false);
  if (!(r.balance > 0)) return null;
  const late = r.promiseOverdueDays > 0;
  const save = async (clear) => {
    setBusy(true);
    try {
      await api.patch(`/finance/qb-invoices/${r.id}`, { promiseDate: clear ? "" : date, promiseNote: note });
      toast.success(clear ? (zh ? "已清除承诺付款日" : "Cleared") : (zh ? `已记录承诺付款日 ${fmtD(date)}` : "Saved"));
      setOpen(false); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const cls = !r.promiseDate ? "text-slate-400 hover:text-slate-700 border-dashed border-slate-200" : late ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) { setDate(r.promiseDate || ""); setNote(r.promiseNote || ""); } }}>
      <PopoverTrigger asChild>
        <button className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-md border ${cls}`} title={r.promiseNote || (zh ? "记录客户承诺付款日" : "Promise to pay")} data-testid={`inv-promise-${r.id}`}>
          <CalendarCheck className="h-3 w-3" />
          {!r.promiseDate ? (zh ? "承诺日" : "Promise") : late ? (zh ? `承诺已过期 ${r.promiseOverdueDays} 天` : `Promise ${r.promiseOverdueDays}d late`) : (zh ? `承诺 ${fmtD(r.promiseDate)}` : `Promised ${fmtD(r.promiseDate)}`)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3 space-y-2 text-xs" data-testid={`inv-promise-pop-${r.id}`}>
        <p className="font-semibold text-slate-800">{zh ? "客户承诺付款日" : "Promise to pay"} · #{r.invoiceNo}</p>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full h-8 rounded-md border border-slate-200 px-2" data-testid={`inv-promise-date-${r.id}`} />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={zh ? "备注（如：业主电话说月底付）" : "Note"} className="w-full h-8 rounded-md border border-slate-200 px-2" data-testid={`inv-promise-note-${r.id}`} />
        {r.promiseDate && <p className="text-[10px] text-slate-400">{zh ? "来源" : "Source"}: {(SRC[r.promiseSource] || SRC.manual)[zh ? 0 : 1]}{r.promiseBy ? ` · ${r.promiseBy}` : ""}{r.promiseAt ? ` · ${fmtDT(r.promiseAt)}` : ""}</p>}
        <div className="flex justify-end gap-1.5 pt-1">
          {r.promiseDate && <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => save(true)} data-testid={`inv-promise-clear-${r.id}`}>{zh ? "清除" : "Clear"}</Button>}
          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy || !date} onClick={() => save(false)} data-testid={`inv-promise-save-${r.id}`}>{zh ? "保存" : "Save"}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/* 已付待核：客户称已付款、财务待核对（批量催收默认跳过） */
export const ClaimChip = ({ r, zh, api, onChanged }) => {
  if (!r.claimedPaid || !(r.balance > 0)) return null;
  const undo = async () => {
    try { await api.post(`/finance/qb-invoices/${r.id}/claim-paid`, { on: false }); toast.success(zh ? "已取消「已付待核」" : "Cleared"); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <button onClick={undo} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-700 border border-violet-200" title={`${r.claimedPaidNote || ""} ${zh ? "点击取消" : "click to clear"}`} data-testid={`inv-claimed-${r.id}`}>
      <BadgeCheck className="h-3 w-3" />{zh ? "已付待核" : "Claims paid"} · {fmtD(r.claimedPaidAt)}
    </button>
  );
};

/* 客户已回复：chip → 弹层看回复原文 + 快捷操作（记录承诺日 / 标记已付待核 / 回复短信） */
export const ReplyChip = ({ r, zh, api, onChanged }) => {
  const [mode, setMode] = useState("");
  const [date, setDate] = useState("");
  const [sms, setSms] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!r.replied) return null;
  const list = [...(r.replies || [])].reverse();
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setMode(""); onChanged?.(); } catch (e) { toast.error(e?.detailText || e.message); }
    setBusy(false);
  };
  const openSms = async () => {
    setMode("sms");
    try { const { data } = await api.post(`/finance/qb-invoices/${r.id}/reply-sms`, { dryRun: true }); setSms(data); } catch (e) { toast.error(e?.detailText || e.message); }
  };
  const act = "h-7 px-2 text-[11px] rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1 text-slate-700 disabled:opacity-50";
  return (
    <Popover onOpenChange={(o) => { if (!o) { setMode(""); setSms(null); } }}>
      <PopoverTrigger asChild>
        <button className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200" title={r.lastReplyBody} data-testid={`inv-replied-${r.id}`}>
          <MessageSquareReply className="h-3 w-3" />{zh ? "客户已回复" : "Replied"} · {fmtD(r.lastReplyAt)}{r.replyCount > 1 ? ` (${r.replyCount})` : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-3 space-y-2 text-xs max-h-[28rem] overflow-auto" data-testid={`inv-replies-pop-${r.id}`}>
        <p className="font-semibold text-slate-800">{zh ? "客户回复" : "Customer replies"} · #{r.invoiceNo}</p>
        {list.map((m) => (
          <div key={m.id} className="rounded-md bg-sky-50/60 border border-sky-100 px-2 py-1.5">
            <div className="flex items-center gap-1 text-[10px] text-slate-500">{m.channel === "email" ? <Mail className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}{m.from} · {fmtDT(m.at)}</div>
            <p className="text-slate-800 whitespace-pre-wrap break-words mt-0.5">{m.body}</p>
          </div>
        ))}
        {!list.length && <p className="text-slate-400">{r.lastReplyBody}</p>}
        {r.balance > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1 border-t border-slate-100" data-testid={`inv-reply-actions-${r.id}`}>
            <button className={act} disabled={busy} onClick={() => { setMode(mode === "promise" ? "" : "promise"); setDate(r.promiseDate || ""); }} data-testid={`inv-reply-act-promise-${r.id}`}><CalendarCheck className="h-3 w-3" />{zh ? "记录承诺日" : "Promise date"}</button>
            <button className={act} disabled={busy || r.claimedPaid} onClick={() => run(() => api.post(`/finance/qb-invoices/${r.id}/claim-paid`, { on: true, note: (r.lastReplyBody || "").slice(0, 120) }), zh ? "已标记「已付待核」" : "Marked")} data-testid={`inv-reply-act-claim-${r.id}`}><BadgeCheck className="h-3 w-3" />{r.claimedPaid ? (zh ? "已标记待核" : "Claimed") : (zh ? "标记已付待核" : "Mark claims paid")}</button>
            <button className={act} disabled={busy} onClick={() => (mode === "sms" ? setMode("") : openSms())} data-testid={`inv-reply-act-sms-${r.id}`}><MessageSquare className="h-3 w-3" />{zh ? "回复短信" : "Reply SMS"}</button>
          </div>
        )}
        {mode === "promise" && (
          <div className="flex-wrap md:flex-nowrap flex items-center gap-1.5" data-testid={`inv-reply-promise-form-${r.id}`}>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 rounded-md border border-slate-200 px-2 flex-1" data-testid={`inv-reply-promise-date-${r.id}`} />
            <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy || !date} onClick={() => run(() => api.patch(`/finance/qb-invoices/${r.id}`, { promiseDate: date, promiseNote: (r.lastReplyBody || "").slice(0, 120) }), zh ? `已记录承诺付款日 ${fmtD(date)}` : "Saved")} data-testid={`inv-reply-promise-save-${r.id}`}>{zh ? "保存" : "Save"}</Button>
          </div>
        )}
        {mode === "sms" && (
          <div className="space-y-1.5" data-testid={`inv-reply-sms-form-${r.id}`}>
            {!sms ? <p className="text-slate-400">…</p> : (
              <>
                <p className="text-[10px] text-slate-500">{zh ? "收件人" : "To"}: <b data-testid={`inv-reply-sms-to-${r.id}`}>{sms.to || (zh ? "（无手机号）" : "(no phone)")}</b> · {sms.lang?.toUpperCase()}{!sms.smsConfigured && <span className="text-red-600 ml-1">{zh ? "短信服务未配置" : "SMS not configured"}</span>}</p>
                <textarea value={sms.text} onChange={(e) => setSms({ ...sms, text: e.target.value })} rows={4} className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid={`inv-reply-sms-text-${r.id}`} />
                <div className="flex justify-end"><Button size="sm" className="h-8 text-xs bg-sky-600 hover:bg-sky-700 text-white" disabled={busy || !sms.to || !sms.smsConfigured || !sms.text.trim()} onClick={() => run(() => api.post(`/finance/qb-invoices/${r.id}/reply-sms`, { dryRun: false, text: sms.text }), zh ? "短信已发送" : "Sent")} data-testid={`inv-reply-sms-send-${r.id}`}>{zh ? "发送短信" : "Send SMS"}</Button></div>
              </>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
